// src/Components/modules/patients/Form/ResponsableItem.js

import React from "react";
import PropTypes from "prop-types";
import { Card, Form, Input, Select, Row, Col, Button, Typography, Badge } from "antd";
import {
  UserOutlined,
  IdcardOutlined,
  PhoneOutlined,
  DeleteOutlined,
  CrownOutlined,
} from "@ant-design/icons";

const { Text } = Typography;
const { Option } = Select;

/**
 * Componente individual para un responsable dentro de la lista.
 */
const ResponsableItem = ({
  index,
  field,
  remove,
  isFirst,
  canDelete,
  tiposDocumento,
}) => {
  // Lista de parentescos
  const parentescos = [
    "Padre",
    "Madre",
    "Hijo(a)",
    "Abuelo(a)",
    "Hermano(a)",
    "Tío(a)",
    "Sobrino(a)",
    "Nieto(a)",
    "Primo(a)",
    "Cónyuge (Esposo/a)",
    "Pareja",
    "Cuñado(a)",
    "Suegro(a)",
    "Yerno",
    "Nuera",
    "Padrastro",
    "Madrastra",
    "Hijastro(a)",
    "Tutor(a) legal",
    "Representante legal",
    "Amigo(a)",
    "Vecino(a)",
    "Compañero(a) de trabajo",
    "Cuidador(a)",
  ];

  return (
    <Card
      size="small"
      style={{
        marginBottom: 12,
        border: isFirst ? "2px solid #1890ff" : "1px solid #d9d9d9",
        borderRadius: 8,
      }}
      bodyStyle={{ padding: 12 }}
    >
      <Row gutter={[12, 8]} align="middle">
        {/* Header del responsable */}
        <Col span={24}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {isFirst ? (
                <Badge
                  count={<CrownOutlined style={{ color: "#faad14" }} />}
                  offset={[-5, 5]}
                >
                  <Text strong style={{ fontSize: 14 }}>
                    Responsable Principal
                  </Text>
                </Badge>
              ) : (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Responsable {index + 1}
                </Text>
              )}
            </div>
            {canDelete && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => remove(field.name)}
                size="small"
              />
            )}
          </div>
        </Col>

        {/* Nombre (requerido) */}
        <Col xs={24} sm={12}>
          <Form.Item
            {...field}
            name={[field.name, "nombre"]}
            label="Nombre completo"
            rules={[
              { required: true, message: "Ingrese el nombre" },
              {
                pattern: /^[A-Za-zÀ-ÿ\s]+$/,
                message: "Solo letras y espacios",
              },
            ]}
            style={{ marginBottom: 8 }}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Nombre del responsable"
            />
          </Form.Item>
        </Col>

        {/* Parentesco */}
        <Col xs={24} sm={12}>
          <Form.Item
            {...field}
            name={[field.name, "parentesco"]}
            label="Parentesco"
            rules={[
              { required: true, message: "Seleccione el parentesco" },
            ]}
            style={{ marginBottom: 8 }}
          >
            <Select placeholder="Seleccione">
              {parentescos.map((p) => (
                <Option key={p} value={p}>
                  {p}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* Tipo de documento */}
        <Col xs={24} sm={8}>
          <Form.Item
            {...field}
            name={[field.name, "tipo_documento_id"]}
            label="Tipo documento"
            rules={[
              { required: true, message: "Seleccione el tipo de documento" },
            ]}
            style={{ marginBottom: 8 }}
          >
            <Select placeholder="Tipo">
              {tiposDocumento.map((tipo) => (
                <Option key={tipo.id} value={tipo.id}>
                  {tipo.descripcion}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* Número de documento */}
        <Col xs={24} sm={8}>
          <Form.Item
            {...field}
            name={[field.name, "numero_documento"]}
            label="Número documento"
            rules={[
              { required: true, message: "Ingrese el número de documento" },
            ]}
            style={{ marginBottom: 8 }}
          >
            <Input
              prefix={<IdcardOutlined />}
              placeholder="Documento"
            />
          </Form.Item>
        </Col>

        {/* Teléfono */}
        <Col xs={24} sm={8}>
          <Form.Item
            {...field}
            name={[field.name, "telefono"]}
            label="Teléfono"
            rules={[
              { required: true, message: "Ingrese el teléfono" },
              {
                pattern: /^\d{10}$/,
                message: "10 dígitos",
              },
            ]}
            style={{ marginBottom: 8 }}
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder="Teléfono"
              maxLength={10}
            />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};

ResponsableItem.propTypes = {
  index: PropTypes.number.isRequired,
  field: PropTypes.object.isRequired,
  remove: PropTypes.func.isRequired,
  isFirst: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
  tiposDocumento: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      descripcion: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default ResponsableItem;
